import { Event } from '../types/event.types';

export interface FamilyMember {
  id: string;
  type: 'adult' | 'child';
  firstName: string;
  lastName: string;
  age?: number;
}

export interface EventValidationResult {
  canCheckIn: boolean;
  warnings: string[];
  incompatibleMembers: FamilyMember[];
  compatibleMembers: FamilyMember[];
}

export const getBasicAgeRequirementDescription = (event: Event): string => {
  if (!event.ageRequirements) return 'events.ageRequirements.allAges';

  const { allowsAdults, allowsChildren } = event.ageRequirements;

  if (allowsAdults && allowsChildren) {
    return 'events.ageRequirements.allAges';
  } else if (allowsAdults && !allowsChildren) {
    return 'events.ageRequirements.adultsOnly';
  } else if (!allowsAdults && allowsChildren) {
    return 'events.ageRequirements.childrenOnly';
  }

  return 'events.ageRequirements.allAges';
};

export const validateEventAttendees = (event: Event, selectedMembers: FamilyMember[]): EventValidationResult => {
  const warnings: string[] = [];
  const incompatibleMembers: FamilyMember[] = [];
  const compatibleMembers: FamilyMember[] = [];

  if (!event.ageRequirements) {
    return {
      canCheckIn: true,
      warnings: [],
      incompatibleMembers: [],
      compatibleMembers: selectedMembers,
    };
  }

  const { allowsAdults, allowsChildren, minAge, maxAge } = event.ageRequirements;

  selectedMembers.forEach((member) => {
    if (member.type === 'adult' && !allowsAdults) {
      incompatibleMembers.push(member);
      warnings.push(`${member.firstName} ${member.lastName} - Adults are not allowed at this event`);
    } else if (member.type === 'child' && !allowsChildren) {
      incompatibleMembers.push(member);
      warnings.push(`${member.firstName} ${member.lastName} - Children are not allowed at this event`);
    } else if (
      member.type === 'child' &&
      allowsChildren &&
      member.age !== undefined &&
      minAge != null &&
      maxAge != null &&
      (member.age < minAge || member.age > maxAge)
    ) {
      incompatibleMembers.push(member);
      warnings.push(
        `${member.firstName} ${member.lastName} - Age ${member.age} is outside the allowed range (${minAge}-${maxAge} years)`,
      );
    } else {
      compatibleMembers.push(member);
    }
  });

  return {
    canCheckIn: incompatibleMembers.length === 0,
    warnings,
    incompatibleMembers,
    compatibleMembers,
  };
};

export const getAgeRequirementBadgeVariant = (event: Event): 'default' | 'secondary' | 'warning' | 'destructive' => {
  if (!event.ageRequirements) return 'secondary';

  const { allowsAdults, allowsChildren, minAge, maxAge, requiresGuardian } = event.ageRequirements;

  if ((allowsAdults && !allowsChildren) || (!allowsAdults && allowsChildren)) {
    return 'destructive';
  }

  if ((minAge !== null && maxAge !== null) || requiresGuardian) {
    return 'warning';
  }

  return 'secondary';
};

export const getIncompatibilityWarning = (event: Event, selectedMembers: FamilyMember[]): string | null => {
  const validation = validateEventAttendees(event, selectedMembers);

  if (validation.canCheckIn) return null;

  const incompatibleCount = validation.incompatibleMembers.length;
  const totalCount = selectedMembers.length;

  if (incompatibleCount === totalCount) {
    return 'None of your selected members can attend this event due to age restrictions.';
  } else {
    return `${incompatibleCount} of ${totalCount} selected members cannot attend this event due to age restrictions.`;
  }
};
